import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { ClearFilters } from '../../../cdl/Filter/ClearFilters/ClearFilters';
import { FilterMultiSelectFull } from '../../../cdl/Filter/FilterMultiSelectFull/FilterMultiSelectFull';
import { FilterText } from '../../../cdl/Filter/FilterText/FilterText';
import { queries } from '../../../common/api/queryKeys/queries';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Box } from '../../../common/ui/Box';
import { useAssignedSuppliersOptions } from '../../../contracts/overview/hooks/useAssignedSuppliersOptions';

export const VesselFilterPills = () => {
    const search = useSearch({ from: '/_app/vessels' });
    const navigate = useNavigate({ from: '/vessels' });
    const role = useRole();
    const isSuperAdmin = role.isSuperAdmin();
    const suppliers = useAssignedSuppliersOptions();
    const customers = useQuery({ ...queries.customers.list(), enabled: isSuperAdmin });
    const companyOptions = (isSuperAdmin ? customers.data ?? [] : role.getCompanies()).map((company) => ({
        value: company.id,
        label: formatCompanyName({ company }) || company.name,
    }));
    const hasActiveFilters =
        !!search.searchQuery.trim() || [search.customerIds, search.defaultSupplierIds].some((ids) => ids.length > 0);

    return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={8} marginBottom={8}>
            <FilterText
                label={translate('vessel.filters.search')}
                filterByLabel={translate('vessel.filters.filterBySearch')}
                value={search.searchQuery}
                onChange={(searchQuery) =>
                    navigate({
                        search: (previous) => ({ ...previous, searchQuery: searchQuery?.trim() || '', page: 0 }),
                    })
                }
            />
            {isSuperAdmin || !role.isOneCompanyUser() ? (
                <FilterMultiSelectFull
                    label={translate(isSuperAdmin ? 'contracts.filters.customer' : 'contracts.filters.company')}
                    filterByLabel={translate(
                        isSuperAdmin ? 'contracts.filters.filterByCustomer' : 'contracts.filters.filterByCompany'
                    )}
                    filterPlaceholder={translate(
                        isSuperAdmin ? 'contracts.filters.searchForCustomer' : 'contracts.filters.searchForCompany'
                    )}
                    options={companyOptions}
                    values={search.customerIds}
                    onChange={(values) =>
                        navigate({ search: (previous) => ({ ...previous, customerIds: values, page: 0 }) })
                    }
                    isLoading={isSuperAdmin && customers.isPending}
                    isError={isSuperAdmin && customers.isError}
                />
            ) : null}
            <FilterMultiSelectFull
                label={translate('vessel.defaultsupplier')}
                filterByLabel={translate('vessel.filters.filterByDefaultSupplier')}
                filterPlaceholder={translate('contracts.filters.searchForSupplier')}
                options={suppliers.data ?? []}
                values={search.defaultSupplierIds}
                onChange={(values) =>
                    navigate({ search: (previous) => ({ ...previous, defaultSupplierIds: values, page: 0 }) })
                }
                isLoading={suppliers.isPending}
                isError={suppliers.isError}
            />
            {hasActiveFilters ? (
                <ClearFilters
                    onClearClick={() =>
                        navigate({
                            search: (previous) => ({
                                ...previous,
                                searchQuery: '',
                                customerIds: [],
                                defaultSupplierIds: [],
                                page: 0,
                            }),
                        })
                    }
                />
            ) : null}
        </Box>
    );
};
